import { useState, useMemo, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import AssignDialog from "../components/AssignDialog";
import CreateAlertDialog from "../components/CreateAlertDialog";
import SanctionBanner from "../components/SanctionBanner";
import SpeedOverlay from "../components/SpeedOverlay";
import LiveNowBanner from "../components/LiveNowBanner";
import FloatingDoorbell from "../components/doorbell/FloatingDoorbell";
import { useDashboardData } from "../hooks/useDashboardData";
import { useDrivingMode } from "../hooks/useDrivingMode";
import { useTrackingSessions } from "../hooks/useTrackingSessions";
import DesktopLayout from "./dashboard/DesktopLayout";
import MobileLayout from "./dashboard/MobileLayout";
import { useDeepLinkAlert } from "./dashboard/useDeepLinkAlert";
import { usePushAction } from "./dashboard/usePushAction";
import { useAlertActions } from "./dashboard/useAlertActions";

export default function Dashboard() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    alerts, userLocation, stats, notifications, proximityRadius,
    updateProximity, setManualLocation, retryGPS, wsConnected,
    fetchAlerts, fetchStats, fetchNotifications,
  } = useDashboardData();
  const [showCreate, setShowCreate] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [mobilePanel, setMobilePanel] = useState(false);
  const [assignAlertId, setAssignAlertId] = useState(null);

  const isAdmin = user?.role === "system_admin" || user?.role === "govt_admin";

  // Read focus location from query params (from Reports page click)
  const queryFocus = useMemo(() => {
    const lat = parseFloat(searchParams.get("lat"));
    const lng = parseFloat(searchParams.get("lng"));
    if (!isNaN(lat) && !isNaN(lng)) return { latitude: lat, longitude: lng };
    return null;
  }, [searchParams]);

  // In-page focus driven by clicking "Show on map" on an alert. We always
  // set a NEW object so the MapView's flyTo effect retriggers even when the
  // user clicks the same alert twice.
  const [feedFocus, setFeedFocus] = useState(null);
  const [focusAlertId, setFocusAlertId] = useState(null);
  const focusLocation = feedFocus || queryFocus;
  const handleAlertFocus = useCallback((alert) => {
    if (typeof alert?.latitude !== "number" || typeof alert?.longitude !== "number") return;
    setFeedFocus({ latitude: alert.latitude, longitude: alert.longitude });
    setFocusAlertId(alert.alert_id);
    // Mobile: scroll to the map so the focus is visible immediately
    if (window.matchMedia && window.matchMedia("(max-width: 1023px)").matches) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  // Reverse direction: a popup opens on the map (user clicked the marker) →
  // highlight the matching card in the AlertFeed. We deliberately do NOT
  // touch `feedFocus` here — only the id — so we don't re-fly the map
  // and create an open→focus→open loop with the marker's auto-open effect.
  const handleMarkerFocus = useCallback((alertId) => {
    setFocusAlertId(alertId);
  }, []);

  // Driving Mode: continuous GPS tracking + TTS announcements for nearby
  // eCautions while in transit. Pipes location updates back through
  // setManualLocation so the user marker, proximity rings, and backend stay
  // in sync with the live position.
  const driving = useDrivingMode({
    alerts,
    ecautionRadiusKm: proximityRadius?.ecaution,
    onLocationUpdate: setManualLocation,
  });

  // Live tracking — registers any active tracking sessions on the network so
  // the map can render moving cyan pins + breadcrumb polylines in real time.
  const trackingSessions = useTrackingSessions(alerts);

  // Safe Route Advisory — the currently-recommended route's polyline (encoded)
  // and metadata, surfaced from SafeRoutePanel. MapView renders the polyline
  // when present so the driver can see the suggested path.
  const [recommendedRoute, setRecommendedRoute] = useState(null);

  // Deep-link from push notification (?alert=<id>)
  useDeepLinkAlert({ alerts, searchParams, setSearchParams, handleAlertFocus });

  // Push-notification action consumers (?resolve=<id>, ?safe=<id>)
  usePushAction({ searchParams, setSearchParams, isAdmin, userLocation, fetchAlerts, fetchStats });

  const handleTrigger = (type) => {
    setSelectedType(type);
    setShowCreate(true);
  };

  const { handleAlertCreated, handleResolve, handleDelete, handleVote } = useAlertActions({
    userLocation, fetchAlerts, fetchStats, setShowCreate,
  });

  const layoutProps = {
    user,
    isAdmin,
    alerts,
    userLocation,
    stats,
    proximityRadius,
    updateProximity,
    setManualLocation,
    retryGPS,
    wsConnected,
    driving,
    recommendedRoute,
    setRecommendedRoute,
    trackingSessions,
    focusLocation,
    focusAlertId,
    onMarkerFocus: handleMarkerFocus,
    onTrigger: handleTrigger,
    onResolve: handleResolve,
    onAssign: setAssignAlertId,
    onDelete: handleDelete,
    onVote: handleVote,
    onAlertFocus: handleAlertFocus,
  };

  return (
    <div className="min-h-screen bg-[#F3F4F6]" data-testid="dashboard-page">
      <Header notifications={notifications} onNotificationRead={fetchNotifications} />
      <SanctionBanner />
      <LiveNowBanner alerts={alerts} />
      <SpeedOverlay
        enabled={driving.enabled}
        currentSpeedKph={driving.currentSpeedKph}
        speedLimitKph={driving.speedLimitKph}
        isOverSpeed={driving.isOverSpeed}
        setSpeedLimitKph={driving.setSpeedLimitKph}
      />

      <DesktopLayout {...layoutProps} />
      <MobileLayout {...layoutProps} mobilePanel={mobilePanel} setMobilePanel={setMobilePanel} />

      <CreateAlertDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        alertType={selectedType}
        userLocation={userLocation}
        onCreated={handleAlertCreated}
      />

      <AssignDialog
        open={!!assignAlertId}
        onClose={() => setAssignAlertId(null)}
        alertId={assignAlertId}
        onAssigned={() => { setAssignAlertId(null); fetchAlerts(); }}
      />

      {/* Doorbell tile — floating + draggable + minimizable. Visible on
          every viewport (phones default to a small bell FAB so the map
          isn't covered, tablets/desktops default to the full tile). */}
      <FloatingDoorbell />
    </div>
  );
}
